import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ImportMessagesTableProps {
  messages: UserMessage[];
  sortField: keyof UserMessage;
  sortDirection: 'asc' | 'desc';
  onSort: (field: keyof UserMessage) => void;
  filters: Partial<Record<keyof UserMessage, string>>;
  onFilterChange: (field: keyof UserMessage, value: string) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLoading?: boolean;
}

export const ImportMessagesTable = ({
  messages,
  sortField,
  sortDirection,
  onSort,
  filters,
  onFilterChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
}: ImportMessagesTableProps) => {
  const { t } = useTranslation();

  // Paginated messages
  const paginatedMessages = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return messages.slice(start, end);
  }, [messages, page, pageSize]);

  const totalPages = Math.ceil(messages.length / pageSize);

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'sent':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const SortableHeader = ({ field, label }: { field: keyof UserMessage; label: string }) => (
    <TableHead>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSort(field)}
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <span>{label}</span>
          <ArrowUpDown className="h-3 w-3" />
        </button>
      </div>
      <Input
        placeholder={t('importMessages.table.filterPlaceholder', { field: label.toLowerCase() })}
        value={filters[field] || ''}
        onChange={(e) => onFilterChange(field, e.target.value)}
        className="mt-2 h-8 text-xs"
      />
    </TableHead>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader field="contactName" label={t('importMessages.table.contactName')} />
              <SortableHeader field="status" label={t('importMessages.table.status')} />
              <SortableHeader field="message" label={t('importMessages.table.message')} />
              <SortableHeader field="dueDate" label={t('importMessages.table.dueTime')} />
              <TableHead>{t('importMessages.table.contactMobile')}</TableHead>
              <SortableHeader field="sentWith" label={t('importMessages.table.sentWith')} />
              <TableHead>{t('importMessages.table.retryCount')}</TableHead>
              <TableHead>{t('importMessages.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {t('importMessages.table.loading')}
                </TableCell>
              </TableRow>
            ) : paginatedMessages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {t('importMessages.table.noMessagesFound')}
                </TableCell>
              </TableRow>
            ) : (
              paginatedMessages.map((message) => {
                const contact = message.contactList?.[0];
                return (
                  <TableRow key={message._id}>
                    <TableCell className="font-medium">
                      {contact?.name || message.contactName || contact?.formattedTitle || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(message.status)}>
                        {message.status || 'unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {message.message || '-'}
                    </TableCell>
                    <TableCell>
                      {message.dueTime 
                        ? new Date(message.dueTime).toLocaleString()
                        : message.dueDate 
                        ? new Date(message.dueDate).toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {contact?.mobile || contact?.id || '-'}
                    </TableCell>
                    <TableCell>
                      {message.sentWith || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {message.retryCount || 0}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Handle view details
                          console.log('View message:', message._id);
                        }}
                      >
                        {t('importMessages.table.view')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {t('importMessages.table.rowsPerPage')}
          </span>
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(0);
            }}
            className="px-2 py-1 border rounded text-sm"
            style={{ 
              borderColor: 'var(--color-border)',
              background: 'var(--color-bg)',
              color: 'var(--color-text)'
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {t('importMessages.table.pageInfo', { current: page + 1, total: totalPages || 1, count: messages.length })}
          </span>
          
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

